import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ScrollView,
  ActivityIndicator,
  SafeAreaView,
  TextInput,
  Modal,
  Linking,
  Dimensions,
  FlatList,
  Platform,
  RefreshControl,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../contexts/ThemeContext";
import { useRoute, RouteProp } from "@react-navigation/native";
import { RootStackParamList } from "../navigation";
import {
  getPublicSchoolOpportunities,
  getSchoolBySlug,
} from "../services/parseService";
import { calculateOpportunityStatus } from "../utils/filterUtils";
import Header from "../components/Header";
import OpportunityCard from "../components/OpportunityCard";
import { Opportunity, OpportunityType } from "../types";

const { width, height } = Dimensions.get("window");
const isMobile = width < 768;
const isSmallMobile = width < 380;

// Responsive columns - same as main page
const numColumns = width > 1200 ? 3 : width > 800 ? 2 : 1;

const OPPORTUNITY_TYPES: OpportunityType[] = [
  "Competition",
  "Scholarship",
  "Hackathon",
  "Internship",
  "Workshop/Programs",
  "Grant",
  "Conference",
  "Other",
];

const getThemedColors = (theme: "light" | "dark") => ({
  background: theme === "dark" ? "#18181B" : "#FAFAFA",
  card: theme === "dark" ? "#232327" : "#FFFFFF",
  text: theme === "dark" ? "#F2F2F2" : "#18181B",
  textSecondary: theme === "dark" ? "#A1A1AA" : "#71717A",
  textMuted: theme === "dark" ? "#71717A" : "#A1A1AA",
  primary: theme === "dark" ? "#F2F2F2" : "#18181B",
  accent: theme === "dark" ? "#60A5FA" : "#3B82F6",
  success: theme === "dark" ? "#34D399" : "#10B981",
  warning: theme === "dark" ? "#FBBF24" : "#F59E0B",
  error: theme === "dark" ? "#F87171" : "#EF4444",
  border: theme === "dark" ? "#3F3F46" : "#E4E4E7",
  chip: theme === "dark" ? "#27272A" : "#F4F4F5",
  chipActive: theme === "dark" ? "#F2F2F2" : "#18181B",
  chipText: theme === "dark" ? "#A1A1AA" : "#71717A",
  chipActiveText: theme === "dark" ? "#18181B" : "#FFFFFF",
  inputBg: theme === "dark" ? "#27272A" : "#FFFFFF",
});

// Type icons for filter chips
const getTypeIcon = (type: string): keyof typeof Ionicons.glyphMap => {
  const icons: Record<string, keyof typeof Ionicons.glyphMap> = {
    Hackathon: "code-slash-outline",
    Competition: "trophy-outline",
    Grant: "cash-outline",
    Scholarship: "school-outline",
    Conference: "people-outline",
    "Workshop/Programs": "construct-outline",
    Internship: "briefcase-outline",
    Other: "star-outline",
  };
  return icons[type] || "star-outline";
};

// Type colors for badges
const getTypeColor = (type: string): string => {
  const colorMap: Record<string, string> = {
    Hackathon: "#F43F5E",
    Competition: "#3B82F6",
    Grant: "#8B5CF6",
    Scholarship: "#10B981",
    Conference: "#334155",
    "Workshop/Programs": "#F59E0B",
    Internship: "#06B6D4",
    Other: "#71717A",
  };
  return colorMap[type] || "#71717A";
};

// Safe type string for display (handles arrays)
const getTypeDisplayString = (type: any): string => {
  if (!type) return "Other";
  if (Array.isArray(type)) return type.join(" / ");
  return String(type);
};

// Safe first type for color lookup
const getFirstType = (type: any): string => {
  if (!type) return "Other";
  if (Array.isArray(type)) return type[0] || "Other";
  return String(type);
};

// Safe initial from name
const getInitial = (name: string): string => {
  return (name && name.length > 0) ? name.charAt(0).toUpperCase() : "?";
};

// Status colors
const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    Open: "#10B981",
    "Coming Soon": "#F59E0B",
    Ongoing: "#3B82F6",
    Closed: "#EF4444",
  };
  return colorMap[status] || "#71717A";
};

// Relative date utilities
const getRelativeDeadline = (date: any): { text: string; isUrgent: boolean; daysLeft: number } => {
  if (!date) return { text: "TBD", isUrgent: false, daysLeft: -1 };

  const deadlineDate = date.toDate ? date.toDate() : new Date(date);
  const now = new Date();
  const diffTime = deadlineDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { text: "Deadline passed", isUrgent: false, daysLeft: diffDays };
  } else if (diffDays === 0) {
    return { text: "Due today!", isUrgent: true, daysLeft: 0 };
  } else if (diffDays === 1) {
    return { text: "Due tomorrow!", isUrgent: true, daysLeft: 1 };
  } else if (diffDays <= 3) {
    return { text: `${diffDays} days left`, isUrgent: true, daysLeft: diffDays };
  } else if (diffDays <= 7) {
    return { text: `${diffDays} days left`, isUrgent: false, daysLeft: diffDays };
  } else if (diffDays <= 14) {
    return { text: `${Math.ceil(diffDays / 7)} weeks left`, isUrgent: false, daysLeft: diffDays };
  } else if (diffDays <= 30) {
    return { text: `${Math.ceil(diffDays / 7)} weeks left`, isUrgent: false, daysLeft: diffDays };
  } else {
    const months = Math.ceil(diffDays / 30);
    return { text: `${months} month${months > 1 ? 's' : ''} left`, isUrgent: false, daysLeft: diffDays };
  }
};

type StudentPortalRouteProp = RouteProp<RootStackParamList, "StudentPortal">;

interface TeacherInfo {
  name: string;
  email: string;
  department?: string;
  roomNumber?: string;
  availability?: string;
  notes?: string;
}

// Type for Parse.Object methods we use
interface ParseOpportunityObject {
  id: string;
  get: (key: string) => any;
  toJSON?: () => Record<string, any>;
}

interface ParseSchoolObject {
  id: string;
  get: (key: string) => any;
}

interface OpportunityWithTeachers {
  opportunity: ParseOpportunityObject;
  teachers: TeacherInfo[];
}

// Helper to safely convert date to ISO string
const toISOString = (date: any): string => {
  if (!date) return "";
  if (typeof date === "string") return date;
  if (date.toISOString) return date.toISOString();
  if (date.toDate) return date.toDate().toISOString();
  return "";
};

// Transform Parse object to Opportunity type for OpportunityCard
const transformToOpportunity = (parseObj: ParseOpportunityObject): Opportunity => {
  // Build the base opportunity without status first
  const baseOpportunity: Omit<Opportunity, "status"> = {
    id: parseObj.id,
    title: parseObj.get("title") || "",
    description: parseObj.get("description") || "",
    type: parseObj.get("type") || "Other",
    startDate: toISOString(parseObj.get("startDate")),
    endDate: toISOString(parseObj.get("endDate")),
    location: parseObj.get("location") || "",
    organization: parseObj.get("organization") || "",
    website: parseObj.get("website") || undefined,
    eligibility: parseObj.get("eligibility") || undefined,
    applicationOpenDate: toISOString(parseObj.get("applicationOpenDate")) || undefined,
    applicationCloseDate: toISOString(parseObj.get("applicationCloseDate")) || undefined,
    deadline: toISOString(parseObj.get("deadline")) || undefined,
    cost: parseObj.get("cost") || undefined,
    tags: parseObj.get("tags") || [],
    isYearlyRecurring: parseObj.get("isYearlyRecurring") || false,
    gradeLevel: parseObj.get("gradeLevel") || [],
    interactionTypes: parseObj.get("interactionTypes") || [],
    isClaimed: parseObj.get("isClaimed") || false,
    createdAt: toISOString(parseObj.get("createdAt")),
    updatedAt: toISOString(parseObj.get("updatedAt")),
  };

  // Now calculate status with the full opportunity object
  return {
    ...baseOpportunity,
    status: calculateOpportunityStatus(baseOpportunity as Opportunity),
  };
};

const StudentPortal: React.FC = () => {
  const { theme } = useTheme();
  const colors = useMemo(() => getThemedColors(theme), [theme]);
  const route = useRoute<StudentPortalRouteProp>();
  const { schoolSlug } = route.params;

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [school, setSchool] = useState<ParseSchoolObject | null>(null);
  const [opportunities, setOpportunities] = useState<OpportunityWithTeachers[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<OpportunityType | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<OpportunityWithTeachers | null>(null);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const isMountedRef = useRef(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search term (300ms delay)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Filter opportunities using useMemo (not useEffect + setState)
  const filteredOpportunities = useMemo(() => {
    let filtered = [...opportunities];

    // Search filter (uses debounced term)
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const title = item.opportunity.get("title")?.toLowerCase() || "";
        const description = item.opportunity.get("description")?.toLowerCase() || "";
        const organization = item.opportunity.get("organization")?.toLowerCase() || "";
        const tags = item.opportunity.get("tags") || [];
        const tagsMatch = tags.some((tag: string) => tag.toLowerCase().includes(term));
        return title.includes(term) || description.includes(term) || organization.includes(term) || tagsMatch;
      });
    }

    // Type filter
    if (selectedType) {
      filtered = filtered.filter((item) => {
        const type = item.opportunity.get("type");
        if (!type) return false;
        const types = Array.isArray(type) ? type : [type];
        return types.includes(selectedType);
      });
    }

    return filtered;
  }, [opportunities, debouncedSearchTerm, selectedType]);

  // Helper to get status from Parse opportunity object
  const getOpportunityStatus = useCallback((parseObj: ParseOpportunityObject): string => {
    return calculateOpportunityStatus(transformToOpportunity(parseObj));
  }, []);

  // Load data with cleanup to prevent race conditions
  useEffect(() => {
    isMountedRef.current = true;

    // Close modal when school changes
    setShowTeacherModal(false);
    setSelectedOpportunity(null);

    // Reset state for new school
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedType(null);

    loadSchoolData();

    return () => {
      isMountedRef.current = false;
    };
  }, [schoolSlug]);

  const loadSchoolData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const schoolData = await getSchoolBySlug(schoolSlug);

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      if (!schoolData) {
        setError("School not found");
        return;
      }

      setSchool(schoolData);
      const opps = await getPublicSchoolOpportunities(schoolSlug);

      // Check again after async operation
      if (!isMountedRef.current) return;

      setOpportunities(opps);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error("Error loading school data:", err);
      setError("Failed to load school opportunities");
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  // Pull to refresh handler
  const handleRefresh = useCallback(() => {
    loadSchoolData(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSlug]);

  // Handle opportunity card press - show teacher modal
  const handleOpportunityPress = useCallback((id: string) => {
    const item = opportunities.find((o) => o.opportunity.id === id);
    if (item) {
      setSelectedOpportunity(item);
      setShowTeacherModal(true);
    }
  }, [opportunities]);

  const handleCloseTeacherModal = () => {
    setShowTeacherModal(false);
    setSelectedOpportunity(null);
  };

  const handleContactTeacher = (email: string) => {
    Linking.openURL(`mailto:${email}`);
  };

  const formatDate = (date: any) => {
    if (!date) return "TBD";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Stats
  const stats = useMemo(() => {
    const openCount = opportunities.filter(
      (item) => getOpportunityStatus(item.opportunity) === "Open"
    ).length;
    const totalTeachers = new Set(
      opportunities.flatMap((item) => item.teachers.map((t) => t.email))
    ).size;
    return { total: opportunities.length, open: openCount, teachers: totalTeachers };
  }, [opportunities, getOpportunityStatus]);

  // Render opportunity card using the shared component
  const renderOpportunityItem = useCallback(
    ({ item, index }: { item: OpportunityWithTeachers; index: number }) => (
      <OpportunityCard
        opportunity={transformToOpportunity(item.opportunity)}
        onPress={handleOpportunityPress}
        index={index}
      />
    ),
    [handleOpportunityPress]
  );

  // Skeleton card component for loading state
  const SkeletonCard = () => (
    <View style={[styles.skeletonCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <View style={styles.skeletonHeader}>
        <View style={[styles.skeletonBadge, { backgroundColor: colors.chip }]} />
        <View style={[styles.skeletonBadgeSmall, { backgroundColor: colors.chip }]} />
      </View>
      <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "40%" }]} />
      <View style={[styles.skeletonTitle, { backgroundColor: colors.chip }]} />
      <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "60%", marginTop: 12 }]} />
      <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "80%", marginTop: 8 }]} />
      <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "100%", marginTop: 16 }]} />
      <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "90%", marginTop: 4 }]} />
    </View>
  );

  // Loading state with skeleton
  if (isLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <Header />
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          <View style={styles.pageContainer}>
            {/* Skeleton school header */}
            <View style={[styles.schoolHeader, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={[styles.skeletonTitle, { backgroundColor: colors.chip, width: "60%", marginBottom: 8 }]} />
              <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "80%" }]} />
              <View style={[styles.statsRow, { marginTop: 16 }]}>
                {[1, 2, 3].map((i) => (
                  <View key={i} style={styles.statItem}>
                    <View style={[styles.skeletonStatValue, { backgroundColor: colors.chip }]} />
                    <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: 60 }]} />
                  </View>
                ))}
              </View>
            </View>

            {/* Skeleton search */}
            <View style={[styles.searchBox, { backgroundColor: colors.inputBg, borderColor: colors.border, marginTop: 16 }]}>
              <View style={[styles.skeletonLine, { backgroundColor: colors.chip, width: "100%", height: 20 }]} />
            </View>

            {/* Skeleton filter chips */}
            <View style={[styles.filterScrollContent, { marginTop: 12 }]}>
              {[1, 2, 3, 4].map((i) => (
                <View key={i} style={[styles.skeletonChip, { backgroundColor: colors.chip }]} />
              ))}
            </View>

            {/* Skeleton cards */}
            <View style={{ marginTop: 24 }}>
              {[1, 2, 3].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // Error state
  if (error || !school) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
        <Header />
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={[styles.errorTitle, { color: colors.text }]}>
            {error || "School not found"}
          </Text>
          <Text style={[styles.errorSubtext, { color: colors.textSecondary }]}>
            Please check the URL and try again
          </Text>
          <TouchableOpacity
            style={[styles.retryButton, { backgroundColor: colors.primary }]}
            onPress={() => loadSchoolData()}
          >
            <Ionicons name="refresh" size={18} color={colors.chipActiveText} />
            <Text style={[styles.retryButtonText, { color: colors.chipActiveText }]}>
              Try Again
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      <Header />
      
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        <View style={styles.pageContainer}>
          {/* School Header */}
          <View style={[styles.schoolHeader, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {/* Powered by badge */}
            {/* <View style={[styles.poweredByBadge, { backgroundColor: colors.chip }]}>
              <Ionicons name="sparkles" size={12} color={colors.accent} />
              <Text style={[styles.poweredByText, { color: colors.textSecondary }]}>
                Powered by BC STEM Database
              </Text>
            </View> */}

            <View style={styles.schoolInfo}>
              <Text style={[styles.schoolName, { color: colors.text }]}>
                {school.get("name")}
              </Text>
              <Text style={[styles.schoolTagline, { color: colors.textSecondary }]}>
                STEM Opportunities with Teacher Support
              </Text>
            </View>

            <View style={styles.statsRow}>
              <View style={styles.statItem}>
                <View style={[styles.statIconBg, { backgroundColor: colors.chip }]}>
                  <Ionicons name="bulb-outline" size={16} color={colors.text} />
                </View>
                <Text style={[styles.statValue, { color: colors.text }]}>{stats.total}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Opportunities</Text>
              </View>
              <View style={[styles.statDivider, { backgroundColor: colors.border }]} />
              <View style={styles.statItem}>
                <View style={[styles.statIconBg, { backgroundColor: colors.success + "20" }]}>
                  <Ionicons name="checkmark-circle-outline" size={16} color={colors.success} />
                </View>
                <Text style={[styles.statValue, { color: colors.success }]}>{stats.open}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Open Now</Text>
              </View>
              <View style={[styles.statDivider, { backgroundColor: colors.border }]} />
              <View style={styles.statItem}>
                <View style={[styles.statIconBg, { backgroundColor: colors.accent + "20" }]}>
                  <Ionicons name="people-outline" size={16} color={colors.accent} />
                </View>
                <Text style={[styles.statValue, { color: colors.accent }]}>{stats.teachers}</Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>Teachers</Text>
              </View>
            </View>

            {school.get("website") && (
              <TouchableOpacity
                style={[styles.websiteLink, { borderColor: colors.border }]}
                onPress={() => Linking.openURL(school.get("website"))}
              >
                <Ionicons name="globe-outline" size={16} color={colors.accent} />
                <Text style={[styles.websiteLinkText, { color: colors.accent }]}>
                  Visit School Website
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Search Bar */}
          <View style={styles.searchSection}>
            <View style={[styles.searchBox, { backgroundColor: colors.inputBg, borderColor: colors.border }]}>
              <Ionicons name="search" size={20} color={colors.textMuted} />
              <TextInput
                style={[styles.searchInput, { color: colors.text }]}
                placeholder="Search opportunities..."
                placeholderTextColor={colors.textMuted}
                value={searchTerm}
                onChangeText={setSearchTerm}
              />
              {searchTerm.length > 0 && (
                <TouchableOpacity onPress={() => setSearchTerm("")}>
                  <Ionicons name="close-circle" size={20} color={colors.textMuted} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Filter Chips */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filterScroll}
            contentContainerStyle={styles.filterScrollContent}
          >
            <TouchableOpacity
              style={[
                styles.filterChip,
                {
                  backgroundColor: selectedType === null ? colors.chipActive : colors.chip,
                  borderColor: selectedType === null ? colors.chipActive : colors.border,
                },
              ]}
              onPress={() => setSelectedType(null)}
            >
              <Ionicons
                name="apps-outline"
                size={14}
                color={selectedType === null ? colors.chipActiveText : colors.chipText}
              />
              <Text
                style={[
                  styles.filterChipText,
                  { color: selectedType === null ? colors.chipActiveText : colors.chipText },
                ]}
              >
                All
              </Text>
            </TouchableOpacity>
            {OPPORTUNITY_TYPES.map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.filterChip,
                  {
                    backgroundColor: selectedType === type ? colors.chipActive : colors.chip,
                    borderColor: selectedType === type ? colors.chipActive : colors.border,
                  },
                ]}
                onPress={() => setSelectedType(selectedType === type ? null : type)}
              >
                <Ionicons
                  name={getTypeIcon(type)}
                  size={14}
                  color={selectedType === type ? colors.chipActiveText : colors.chipText}
                />
                <Text
                  style={[
                    styles.filterChipText,
                    { color: selectedType === type ? colors.chipActiveText : colors.chipText },
                  ]}
                >
                  {type}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Results Count */}
          <View style={styles.resultsHeader}>
            <Text style={[styles.resultsCount, { color: colors.textSecondary }]}>
              {filteredOpportunities.length} {filteredOpportunities.length === 1 ? "opportunity" : "opportunities"}
              {selectedType && ` in ${selectedType}`}
              {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
            </Text>
            {(debouncedSearchTerm || selectedType) && (
              <TouchableOpacity
                onPress={() => {
                  setSearchTerm("");
                  setSelectedType(null);
                }}
              >
                <Text style={[styles.clearFilters, { color: colors.accent }]}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Opportunities Grid */}
          {filteredOpportunities.length > 0 ? (
            <FlatList
              data={filteredOpportunities}
              renderItem={renderOpportunityItem}
              keyExtractor={(item, index) => item.opportunity?.id || `opp-${index}`}
              numColumns={numColumns > 1 ? numColumns : 1}
              columnWrapperStyle={numColumns > 1 ? styles.gridWrapper : undefined}
              contentContainerStyle={styles.gridContainer}
              scrollEnabled={false}
              removeClippedSubviews={Platform.OS !== 'web'}
              maxToRenderPerBatch={10}
              windowSize={5}
              initialNumToRender={6}
              getItemLayout={numColumns === 1 ? (_, index) => ({
                length: 320,
                offset: 320 * index,
                index,
              }) : undefined}
            />
          ) : (
            <View style={styles.emptyState}>
              <Ionicons
                name={debouncedSearchTerm || selectedType ? "search-outline" : "folder-open-outline"}
                size={48}
                color={colors.textMuted}
              />
              <Text style={[styles.emptyTitle, { color: colors.text }]}>
                {debouncedSearchTerm || selectedType ? "No matches found" : "No opportunities available"}
              </Text>
              <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                {debouncedSearchTerm || selectedType
                  ? "Try adjusting your search or filters"
                  : "Your school hasn't added any opportunities yet.\nAsk your teachers to add some!"}
              </Text>
              {(debouncedSearchTerm || selectedType) && (
                <TouchableOpacity
                  style={[styles.clearFiltersButton, { borderColor: colors.border }]}
                  onPress={() => {
                    setSearchTerm("");
                    setSelectedType(null);
                  }}
                >
                  <Ionicons name="refresh-outline" size={16} color={colors.accent} />
                  <Text style={[styles.clearFiltersButtonText, { color: colors.accent }]}>
                    Clear filters
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Teacher Info Note */}
          <View style={[styles.teacherInfoNote, { backgroundColor: colors.chip, borderColor: colors.border }]}>
            <Ionicons name="information-circle-outline" size={20} color={colors.accent} />
            <Text style={[styles.teacherInfoText, { color: colors.textSecondary }]}>
              Click on any opportunity to see which teachers can help you apply
            </Text>
          </View>
        </View>
      </ScrollView>

      {/* Opportunity Detail Modal */}
      <Modal
        visible={showTeacherModal}
        animationType="slide"
        transparent
        onRequestClose={handleCloseTeacherModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseTeacherModal}>
          <View style={[styles.modalOverlay, { backgroundColor: "rgba(0,0,0,0.6)" }]}>
            <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
              <View style={[styles.modalContent, { backgroundColor: colors.background }]}>
            {/* Modal Header with colored accent */}
            {selectedOpportunity && (
              <View style={[
                styles.modalHeader, 
                { 
                  backgroundColor: colors.card,
                  borderBottomColor: colors.border,
                }
              ]}>
                <View style={[styles.modalHeaderAccent, { backgroundColor: getTypeColor(getFirstType(selectedOpportunity.opportunity.get("type"))) }]} />
                <View style={styles.modalDragHandle} />
                <TouchableOpacity style={styles.modalCloseButton} onPress={handleCloseTeacherModal}>
                  <Ionicons name="close" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            )}

            <ScrollView 
              style={styles.modalBody} 
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.modalBodyContent}
            >
              {selectedOpportunity && (
                <>
                  {/* Type and Status labels */}
                  <View style={styles.badgeRow}>
                    <Text style={[styles.modalTypeLabel, { color: getTypeColor(getFirstType(selectedOpportunity.opportunity.get("type"))) }]}>
                      {getTypeDisplayString(selectedOpportunity.opportunity.get("type"))}
                    </Text>
                    <Text style={[styles.modalStatusDot, { color: colors.textMuted }]}>•</Text>
                    <Text style={[styles.modalStatusLabel, { color: getStatusColor(getOpportunityStatus(selectedOpportunity.opportunity)) }]}>
                      {getOpportunityStatus(selectedOpportunity.opportunity)}
                    </Text>
                  </View>

                  {/* Title and Organization */}
                  <Text style={[styles.modalTitle, { color: colors.text }]}>
                    {selectedOpportunity.opportunity.get("title")}
                  </Text>
                  <View style={styles.orgRow}>
                    <Ionicons name="business-outline" size={16} color={colors.textSecondary} />
                    <Text style={[styles.modalOrg, { color: colors.textSecondary }]}>
                      {selectedOpportunity.opportunity.get("organization")}
                    </Text>
                  </View>

                  {/* Teacher summary line */}
                  <View style={styles.teacherSummaryRow}>
                    <Ionicons
                      name="people-outline"
                      size={16}
                      color={selectedOpportunity.teachers.length > 0 ? colors.success : colors.textMuted}
                    />
                    <Text style={[
                      styles.teacherSummaryText,
                      { color: selectedOpportunity.teachers.length > 0 ? colors.success : colors.textMuted }
                    ]}>
                      {selectedOpportunity.teachers.length > 0
                        ? `${selectedOpportunity.teachers.length} teacher${selectedOpportunity.teachers.length > 1 ? 's' : ''} available at your school`
                        : "No teacher support yet"}
                    </Text>
                  </View>

                  {/* Deadline alert */}
                  {(() => {
                    const deadlineDate = selectedOpportunity.opportunity.get("applicationCloseDate") || selectedOpportunity.opportunity.get("deadline");
                    if (!deadlineDate) return null;
                    const relativeInfo = getRelativeDeadline(deadlineDate);
                    if (relativeInfo.daysLeft < 0) return null;
                    return (
                      <View style={[styles.deadlineAlert, { backgroundColor: (relativeInfo.isUrgent ? colors.error : colors.warning) + "12", borderColor: relativeInfo.isUrgent ? colors.error : colors.warning }]}>
                        <Ionicons name={relativeInfo.isUrgent ? "alert-circle" : "time-outline"} size={16} color={relativeInfo.isUrgent ? colors.error : colors.warning} />
                        <Text style={[styles.deadlineAlertText, { color: relativeInfo.isUrgent ? colors.error : colors.warning }]}>
                          {formatDate(deadlineDate)} — {relativeInfo.text}
                        </Text>
                      </View>
                    );
                  })()}

                  {/* Key info rows */}
                  <View style={[styles.infoSection, { borderBottomColor: colors.border }]}>
                    {selectedOpportunity.opportunity.get("startDate") && (
                      <View style={styles.infoItem}>
                        <Ionicons name="calendar-outline" size={16} color={colors.textSecondary} />
                        <Text style={[styles.infoItemLabel, { color: colors.textSecondary }]}>Dates</Text>
                        <Text style={[styles.infoItemValue, { color: colors.text }]}>
                          {formatDate(selectedOpportunity.opportunity.get("startDate"))}
                          {selectedOpportunity.opportunity.get("endDate") &&
                           selectedOpportunity.opportunity.get("endDate") !== selectedOpportunity.opportunity.get("startDate") &&
                            ` – ${formatDate(selectedOpportunity.opportunity.get("endDate"))}`}
                        </Text>
                      </View>
                    )}
                    {selectedOpportunity.opportunity.get("location") && (
                      <View style={styles.infoItem}>
                        <Ionicons name="location-outline" size={16} color={colors.textSecondary} />
                        <Text style={[styles.infoItemLabel, { color: colors.textSecondary }]}>Location</Text>
                        <Text style={[styles.infoItemValue, { color: colors.text }]}>
                          {selectedOpportunity.opportunity.get("location")}
                        </Text>
                      </View>
                    )}
                    {selectedOpportunity.opportunity.get("cost") && (
                      <View style={styles.infoItem}>
                        <Ionicons name="cash-outline" size={16} color={colors.textSecondary} />
                        <Text style={[styles.infoItemLabel, { color: colors.textSecondary }]}>Cost</Text>
                        <Text style={[styles.infoItemValue, { color: colors.text }]}>
                          {selectedOpportunity.opportunity.get("cost")}
                        </Text>
                      </View>
                    )}
                    {selectedOpportunity.opportunity.get("gradeLevel")?.length > 0 && (
                      <View style={styles.infoItem}>
                        <Ionicons name="school-outline" size={16} color={colors.textSecondary} />
                        <Text style={[styles.infoItemLabel, { color: colors.textSecondary }]}>Grades</Text>
                        <Text style={[styles.infoItemValue, { color: colors.text }]}>
                          {selectedOpportunity.opportunity.get("gradeLevel").join(", ")}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Description */}
                  {selectedOpportunity.opportunity.get("description") && (
                    <View style={styles.descriptionSection}>
                      <Text style={[styles.descriptionText, { color: colors.textSecondary }]}>
                        {selectedOpportunity.opportunity.get("description")}
                      </Text>
                    </View>
                  )}

                  {/* Eligibility */}
                  {selectedOpportunity.opportunity.get("eligibility") && (
                    <View style={styles.eligibilitySection}>
                      <Text style={[styles.eligibilitySectionTitle, { color: colors.text }]}>Eligibility</Text>
                      <Text style={[styles.eligibilityText, { color: colors.textSecondary }]}>
                        {selectedOpportunity.opportunity.get("eligibility")}
                      </Text>
                    </View>
                  )}

                  {/* Tags */}
                  {selectedOpportunity.opportunity.get("tags") && 
                   selectedOpportunity.opportunity.get("tags").length > 0 && (
                    <View style={styles.tagsSection}>
                      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                        {selectedOpportunity.opportunity.get("tags").map((tag: string, idx: number) => (
                          <View key={idx} style={[styles.tagChip, { backgroundColor: colors.chip, borderColor: colors.border }]}>
                            <Text style={[styles.tagChipText, { color: colors.textSecondary }]}>{tag}</Text>
                          </View>
                        ))}
                      </ScrollView>
                    </View>
                  )}

                  {/* Primary CTA */}
                  {selectedOpportunity.opportunity.get("website") && (
                    <TouchableOpacity
                      style={[styles.primaryCTA, { backgroundColor: colors.accent }]}
                      onPress={() => Linking.openURL(selectedOpportunity.opportunity.get("website"))}
                    >
                      <Ionicons name="open-outline" size={18} color="#FFFFFF" />
                      <Text style={styles.primaryCTAText}>Visit Website</Text>
                    </TouchableOpacity>
                  )}

                  {/* Teacher support section */}
                  <View style={styles.teachersSection}>
                    <Text style={[styles.teachersSectionTitle, { color: colors.text }]}>
                      Teacher Support
                    </Text>

                    {selectedOpportunity.teachers.length === 0 ? (
                      <View style={styles.noTeachersRow}>
                        <Ionicons name="person-outline" size={24} color={colors.textMuted} />
                        <Text style={[styles.noTeachersText, { color: colors.textMuted }]}>
                          No teachers have signed up yet
                        </Text>
                      </View>
                    ) : (
                      selectedOpportunity.teachers.map((teacher, index) => (
                        <View
                          key={index}
                          style={[
                            styles.teacherRow,
                            index < selectedOpportunity.teachers.length - 1 && { borderBottomWidth: 1, borderBottomColor: colors.border },
                          ]}
                        >
                          <View style={styles.teacherRowMain}>
                            <View style={[styles.teacherAvatar, { backgroundColor: colors.accent }]}>
                              <Text style={styles.teacherAvatarText}>
                                {getInitial(teacher.name)}
                              </Text>
                            </View>
                            <View style={styles.teacherRowInfo}>
                              <Text style={[styles.teacherName, { color: colors.text }]}>
                                {teacher.name}
                              </Text>
                              <Text style={[styles.teacherDetailLine, { color: colors.textSecondary }]}>
                                {[teacher.department, teacher.roomNumber && `Room ${teacher.roomNumber}`, teacher.availability].filter(Boolean).join("  ·  ")}
                              </Text>
                            </View>
                            <TouchableOpacity
                              style={[styles.contactIconButton, { backgroundColor: colors.accent + "15" }]}
                              onPress={() => handleContactTeacher(teacher.email)}
                            >
                              <Ionicons name="mail-outline" size={18} color={colors.accent} />
                            </TouchableOpacity>
                          </View>
                          {teacher.notes && (
                            <Text style={[styles.teacherNotes, { color: colors.textMuted }]}>
                              "{teacher.notes}"
                            </Text>
                          )}
                        </View>
                      ))
                    )}
                  </View>

                  {/* Bottom spacing */}
                  <View style={{ height: 20 }} />
                </>
              )}
            </ScrollView>
          </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  pageContainer: {
    maxWidth: 1400,
    width: "100%",
    alignSelf: "center",
    paddingHorizontal: isMobile ? 12 : 24,
  },

  // School Header
  schoolHeader: {
    marginTop: 16,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
  },
  poweredByBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 12,
    gap: 5,
    marginBottom: 12,
  },
  poweredByText: {
    fontSize: 11,
    fontWeight: "500",
  },
  schoolInfo: {
    marginBottom: 20,
  },
  schoolName: {
    fontSize: isMobile ? 22 : 26,
    fontWeight: "bold",
    marginBottom: 4,
  },
  schoolTagline: {
    fontSize: 14,
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  statItem: {
    alignItems: "center",
    paddingHorizontal: isMobile ? 12 : 20,
  },
  statIconBg: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 6,
  },
  statValue: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 11,
  },
  statDivider: {
    width: 1,
    height: 32,
  },
  websiteLink: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    gap: 6,
  },
  websiteLinkText: {
    fontSize: 14,
    fontWeight: "500",
  },

  // Search
  searchSection: {
    marginTop: 16,
  },
  searchBox: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    gap: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },

  // Filters
  filterScroll: {
    marginTop: 12,
  },
  filterScrollContent: {
    gap: 8,
    paddingVertical: 4,
  },
  filterChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    gap: 6,
  },
  filterChipText: {
    fontSize: 13,
    fontWeight: "500",
  },

  // Results
  resultsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
    marginBottom: 8,
  },
  resultsCount: {
    fontSize: 14,
  },
  clearFilters: {
    fontSize: 14,
    fontWeight: "600",
  },

  // Grid
  gridContainer: {
    paddingBottom: 16,
  },
  gridWrapper: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },

  // Empty State
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
  },
  clearFiltersButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 16,
    gap: 6,
  },
  clearFiltersButtonText: {
    fontSize: 14,
    fontWeight: "500",
  },

  // Teacher Info Note
  teacherInfoNote: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    borderRadius: 10,
    marginTop: 8,
    borderWidth: 1,
    gap: 10,
  },
  teacherInfoText: {
    flex: 1,
    fontSize: 13,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },

  // Error
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 16,
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    marginBottom: 24,
  },
  retryButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  retryButtonText: {
    fontSize: 15,
    fontWeight: "600",
  },

  // Modal
  modalOverlay: {
    flex: 1,
    justifyContent: isMobile ? "flex-end" : "center",
    alignItems: isMobile ? "stretch" : "center",
  },
  modalContent: {
    maxHeight: isMobile ? height * 0.92 : height * 0.85,
    width: isMobile ? "100%" : "100%",
    maxWidth: isMobile ? undefined : 640,
    borderTopLeftRadius: isMobile ? 20 : 16,
    borderTopRightRadius: isMobile ? 20 : 16,
    borderBottomLeftRadius: isMobile ? 0 : 16,
    borderBottomRightRadius: isMobile ? 0 : 16,
    overflow: "hidden",
  },
  modalHeader: {
    alignItems: "center",
    paddingTop: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    position: "relative",
  },
  modalHeaderAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
  },
  modalDragHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: "rgba(128, 128, 128, 0.3)",
    marginTop: 2,
  },
  modalCloseButton: {
    position: "absolute",
    right: 16,
    top: 10,
    padding: 8,
  },
  modalBody: {
    flex: 1,
  },
  modalBodyContent: {
    padding: isMobile ? 16 : 24,
  },

  // Type / Status labels
  badgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  modalTypeLabel: {
    fontSize: 13,
    fontWeight: "600",
  },
  modalStatusDot: {
    fontSize: 13,
  },
  modalStatusLabel: {
    fontSize: 13,
    fontWeight: "600",
  },

  // Title and Org
  modalTitle: {
    fontSize: isMobile ? 22 : 26,
    fontWeight: "bold",
    marginBottom: 6,
    lineHeight: isMobile ? 28 : 32,
  },
  orgRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  modalOrg: {
    fontSize: 14,
    flex: 1,
  },

  // Teacher summary line
  teacherSummaryRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 20,
  },
  teacherSummaryText: {
    fontSize: 13,
    fontWeight: "500",
  },

  // Deadline alert
  deadlineAlert: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    marginBottom: 16,
    borderLeftWidth: 3,
    gap: 8,
  },
  deadlineAlertText: {
    fontSize: 13,
    fontWeight: "500",
    flex: 1,
  },

  // Info section
  infoSection: {
    gap: 12,
    paddingBottom: 16,
    marginBottom: 16,
    borderBottomWidth: 1,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  infoItemLabel: {
    fontSize: 13,
    width: 60,
  },
  infoItemValue: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },

  // Description
  descriptionSection: {
    marginBottom: 16,
  },
  descriptionText: {
    fontSize: 14,
    lineHeight: 22,
  },

  // Eligibility
  eligibilitySection: {
    marginBottom: 16,
  },
  eligibilitySectionTitle: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 6,
  },
  eligibilityText: {
    fontSize: 14,
    lineHeight: 20,
  },

  // Tags
  tagsSection: {
    marginBottom: 16,
  },
  tagChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
    marginRight: 8,
    borderWidth: 1,
  },
  tagChipText: {
    fontSize: 12,
  },

  // Primary CTA
  primaryCTA: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 10,
    marginBottom: 24,
    gap: 8,
  },
  primaryCTAText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },

  // Teachers Section
  teachersSection: {
    marginBottom: 8,
  },
  teachersSectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
  },
  noTeachersRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    paddingVertical: 24,
  },
  noTeachersText: {
    fontSize: 14,
  },
  teacherRow: {
    paddingVertical: 14,
  },
  teacherRowMain: {
    flexDirection: "row",
    alignItems: "center",
  },
  teacherAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  teacherAvatarText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  teacherRowInfo: {
    flex: 1,
  },
  teacherName: {
    fontSize: 15,
    fontWeight: "600",
    marginBottom: 2,
  },
  teacherDetailLine: {
    fontSize: 13,
  },
  contactIconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
  },
  teacherNotes: {
    fontSize: 13,
    fontStyle: "italic",
    lineHeight: 18,
    marginTop: 6,
    marginLeft: 52,
  },

  // Skeleton loading styles
  skeletonCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  skeletonHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  skeletonBadge: {
    width: 100,
    height: 28,
    borderRadius: 14,
  },
  skeletonBadgeSmall: {
    width: 70,
    height: 24,
    borderRadius: 12,
  },
  skeletonTitle: {
    height: 24,
    borderRadius: 4,
    width: "80%",
  },
  skeletonLine: {
    height: 14,
    borderRadius: 4,
  },
  skeletonStatValue: {
    width: 40,
    height: 28,
    borderRadius: 4,
    marginBottom: 4,
  },
  skeletonChip: {
    width: 80,
    height: 32,
    borderRadius: 16,
    marginRight: 8,
  },
});

export default StudentPortal;
